<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import LimitAccessAlert from '@/components/LimitAccessAlert.vue'
import PremiumRequired from '@/components/PremiumRequired.vue'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import ContentReviewButton from '@/content-review/components/ContentReviewButton.vue'
import ContentReviewModal from '@/content-review/components/ContentReviewModal.vue'
import ContentReviewResults from '@/content-review/components/ContentReviewResults.vue'
import type { AuditResult } from '@/content-review/api'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getGoalSubjectLabelFromValue } from '@/contentprofile/no_sel_composable'
import { useCustomizationDemoStore } from '@/customization-demo/customization-demo.store'
import { CUSTOMIZATIONS } from '@/customization-demo/customization_demo_catalog'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { AccessPointsUtil, hasEveryAccessPointUuid } from '@/util/access_points'
import { keepingBetaOptIn } from '@/util/beta_opt_in'
import { toolkitUrls } from '@/util/links'
import { setTitle } from '@/util/title'
import { VUE2_CONTAINER_WIDTHS } from '@/util/vue2_layout'
import { useVisibilityStore } from '@/visibility/visibility.store'
import AccessPointTabs from './AccessPointTabs.vue'
import AnchorPageLabel from './AnchorPageLabel.vue'
import ContentPageBreadcrumbs from './ContentPageBreadcrumbs.vue'
import GoalLoadError from './GoalLoadError.vue'
import AnchorAnnualGoal from './anchor-page/AnchorAnnualGoal.vue'
import AnchorPageSidebar from './anchor-page/AnchorPageSidebar.vue'
import AnchorPageTitle from './anchor-page/AnchorPageTitle.vue'
import AnchorQuarterlyObjectives from './anchor-page/AnchorQuarterlyObjectives.vue'
import { useAnchorPageStore } from './anchor_page.store'
import type { AnchorPageContent } from './anchor_page_types'
import { useGoalAutoReview, useGoalCriteriaSetName } from './auto-review/useGoalAutoReview'
import { useGoalTranslationStore } from './goal_translation.store'
import { eachKeyComputed } from './page_event_context'
import { useAnchorPageTracking } from './useAnchorPageTracking'

const DEFAULT_CRITERIA_LABEL = 'default'
const GOAL_PAGE_WIKI_HEADINGS = '[&_[data-testid=wiki-text-block]_h2]:text-display-sm [&_[data-testid=wiki-text-block]_h3]:text-xl'

const props = withDefaults(defineProps<{
  uuid: string
  breadcrumbSubject?: string
  breadcrumbDomain?: string
  isDraft?: boolean
}>(), {
  breadcrumbSubject: '',
  breadcrumbDomain: '',
  isDraft: false
})

const anchorPageStore = useAnchorPageStore()
const {
  content,
  isFetching,
  isPremiumBlocked,
  hasLoadError,
  isGoalCustomized,
  customPrimaryGoalText,
  customObjectives,
  accessInfo,
  hasAccordionObjectives,
  accessPointsLevelUuidMap,
  areAccessPointsPublished,
  contentEventContext
} = storeToRefs(anchorPageStore)
const translationStore = useGoalTranslationStore()
const { profileId } = storeToRefs(useContentProfileStore())
const customizationDemoStore = useCustomizationDemoStore()
const goalDemoProfileId = computed(() => customizationDemoStore.demoProfileFor(CUSTOMIZATIONS.GOALS))
const { editorVisibility } = storeToRefs(useVisibilityStore())
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()
const criteriaSetName = useGoalCriteriaSetName()

const pageEventContext = computed(() => ({
  goal_breadcrumb_subject: props.breadcrumbSubject,
  goal_breadcrumb_domain: props.breadcrumbDomain,
  ...contentEventContext.value,
  criteria_label: criteriaSetName.value || DEFAULT_CRITERIA_LABEL,
  is_custom_criteria_set: Boolean(criteriaSetName.value)
}))
const analytics = getAnalyticsTracker().getChildContext(eachKeyComputed(pageEventContext))
const { trackPageViewed } = useAnchorPageTracking(analytics)

const goalSubjects = computed(() => (content.value?.subject_alignments ?? []).map((alignment) => alignment.subject))
const reviewedGoal = computed(() => ({
  uuid: content.value?.uuid ?? '',
  goalText: customPrimaryGoalText.value ?? '',
  objectiveTexts: customObjectives.value.map((objective) => objective.text),
  grades: content.value?.grades ?? [],
  subjects: goalSubjects.value,
  isCustomized: isGoalCustomized.value
}))
const {
  isModalOpen: isReviewModalOpen,
  isReviewAvailable,
  hasCompletedReview,
  criteriaByType,
  isCriterionVisible,
  reviewButtonText,
  reviewDescriptionLabel,
  goalReviewProps,
  objectivesReviewProps,
  isGoalReviewVisible,
  isObjectivesReviewVisible,
  openModal: openReviewModal,
  review,
  dismissGoalReview,
  dismissObjectivesReview,
  trackResultToggle
} = useGoalAutoReview({ goal: reviewedGoal, analytics })
const ANNUAL_GOAL_EVENT_CONTEXT = { obj_level: 'Annual' }
const trackAnnualResultToggle = (result: AuditResult) => trackResultToggle(result, ANNUAL_GOAL_EVENT_CONTEXT)
const isReviewButtonShown = computed(() => !isFetching.value && isReviewAvailable.value)

const isAccessPointTabsShown = computed(() => {
  const hasAllAccessPoints = hasEveryAccessPointUuid(accessPointsLevelUuidMap.value)
  const isEditorPreview = Boolean(content.value?.access_grade_level_uuid) && editorVisibility.value
  return hasAllAccessPoints && (areAccessPointsPublished.value || isEditorPreview)
})
const annualGoalWrapper = computed(() => (isAccessPointTabsShown.value ? AccessPointTabs : 'div'))
const annualGoalWrapperProps = computed(() => {
  if (!isAccessPointTabsShown.value) return {}
  return { activeAccessPoint: content.value?.access_point_level, accessPointUuids: accessPointsLevelUuidMap.value }
})

const goalLabel = computed(() => {
  const title = content.value?.title ?? ''
  const accessPointLevel = content.value?.access_point_level
  if (!isAccessPointTabsShown.value || !accessPointLevel) return title
  return `${title} (${AccessPointsUtil.labelsMap[accessPointLevel]})`
})

const isDraftLabelShown = computed(() => props.isDraft || !content.value?.published)

const editUrl = computed(() => {
  if (!editorVisibility.value) return ''
  const accessGradeLevelUuid = content.value?.access_grade_level_uuid
  if (accessGradeLevelUuid) return keepingBetaOptIn(toolkitUrls.editorLinks.editAnchorAccessPoints(accessGradeLevelUuid))
  return toolkitUrls.editorLinks.editGoal(props.uuid)
})

const breadcrumbSubjectDomains = computed(() => {
  if (props.breadcrumbDomain) return [props.breadcrumbDomain]
  return content.value?.subject_domains ?? []
})

// Translation mounts only once a load has finished, so a card left open on this page translates its loaded text.
const isReadyToTranslate = ref(false)
let hasSentPageView = false

// Access point tabs reuse this page, and Vue 2 counted the first one shown as the page view.
function sendPageViewOnce(loaded?: AnchorPageContent) {
  if (hasSentPageView) return
  hasSentPageView = true
  trackPageViewed(loaded?.standard_set_refs?.map((ref) => ref.standard_long_ref))
}

function showPage(loaded: AnchorPageContent) {
  setTitle(`${loaded.title} | ${getGoalSubjectLabelFromValue(loaded.subject)} Anchor Page IEP Goal and Objectives`)
  updateFeedbackContext({ uuid: loaded.uuid, title: loaded.title, subject: loaded.subject })
  translationStore.fetchLanguages()
  isReadyToTranslate.value = true
  sendPageViewOnce(loaded)
  anchorPageStore.fetchAllResources()
}

function isInShownAccessPointGroup(uuid: string) {
  return anchorPageStore.isShowing(uuid) || Object.values(accessPointsLevelUuidMap.value).includes(uuid)
}

// The staircase links to unrelated anchor pages through the router, which reuses this page instead of remounting it.
function leaveUnrelatedPage() {
  if (!content.value || isInShownAccessPointGroup(props.uuid)) return
  anchorPageStore.clearPage()
  hasSentPageView = false
}

let isUnmounted = false

async function loadPage() {
  leaveUnrelatedPage()
  isReadyToTranslate.value = false
  const loaded = await anchorPageStore.fetchAnchorPage({ uuid: props.uuid, isDraft: props.isDraft })
  if (isUnmounted) return
  if (loaded) {
    showPage(loaded)
  } else if (anchorPageStore.isForbidden) {
    sendPageViewOnce()
  }
}

watch([() => props.uuid, () => props.isDraft, profileId, goalDemoProfileId], loadPage, { immediate: true })

onBeforeUnmount(() => {
  isUnmounted = true
  anchorPageStore.clearPage()
})
</script>

<template>
  <CopyTrackWrapper type="superpage" :title="content?.title" :uuid="content?.uuid" gate-copy :demo="content?.demo">
    <div class="print:hidden">
      <PremiumRequired v-if="isPremiumBlocked" />
      <GoalLoadError v-else-if="hasLoadError" />
      <div v-else-if="content" class="container pt-4 text-gray-900" :class="[VUE2_CONTAINER_WIDTHS, GOAL_PAGE_WIKI_HEADINGS]" data-testid="anchor-page">
        <ContentPageBreadcrumbs
          class="mb-5"
          :subject="breadcrumbSubject || content.subject || ''"
          :subject-domains="breadcrumbSubjectDomains"
          :grades="content.grades ?? []"
          :title="content.title"
        />

        <div class="md:grid md:grid-cols-12 md:gap-x-8">
          <div class="md:col-span-8">
            <AnchorPageLabel v-if="!hasAccordionObjectives" />
            <AnchorPageTitle :title="content.title" :goal-label="goalLabel" :is-draft="isDraftLabelShown" :edit-url="editUrl" />

            <component :is="annualGoalWrapper" v-bind="annualGoalWrapperProps">
              <AnchorAnnualGoal :goal-label="goalLabel" :is-ready-to-translate="isReadyToTranslate">
                <template #goalReview>
                  <ContentReviewResults
                    v-if="isGoalReviewVisible"
                    v-bind="goalReviewProps"
                    @toggle="trackAnnualResultToggle"
                    @close="dismissGoalReview"
                  />
                </template>
                <template #objectivesReview>
                  <ContentReviewResults
                    v-if="isObjectivesReviewVisible"
                    v-bind="objectivesReviewProps"
                    @toggle="trackAnnualResultToggle"
                    @close="dismissObjectivesReview"
                  />
                </template>
              </AnchorAnnualGoal>
            </component>

            <LimitAccessAlert v-if="accessInfo.blur" class="md:mx-auto md:w-10/12" :access-info="accessInfo" />

            <template v-if="!hasAccordionObjectives">
              <AnchorQuarterlyObjectives />
              <LimitAccessAlert v-if="accessInfo.blur" class="md:mx-auto md:w-10/12" :access-info="accessInfo" />
            </template>
          </div>

          <div class="md:col-span-4">
            <AnchorPageSidebar>
              <template #review>
                <ContentReviewButton
                  v-if="isReviewButtonShown"
                  class="mt-2.5"
                  :criteria-set-name="criteriaSetName"
                  :has-existing-results="hasCompletedReview"
                  @click="openReviewModal"
                />
                <ContentReviewModal
                  v-model:open="isReviewModalOpen"
                  :criteria-by-type="criteriaByType"
                  :criteria-set-name="criteriaSetName"
                  :subjects="goalSubjects"
                  :is-criterion-visible="isCriterionVisible"
                  :review-button-text="reviewButtonText"
                  :description-label="reviewDescriptionLabel"
                  @review="review"
                />
              </template>
            </AnchorPageSidebar>
          </div>
        </div>
      </div>
    </div>
    <div class="hidden pt-24 text-center print:block">This page is intentionally left blank.</div>
  </CopyTrackWrapper>
</template>
